import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import moment from 'moment'
import SizeSelect, { components } from 'react-select'
import ContentHeader from 'component/utils/ContentHeader'
import Modal from 'component/Modal'
import Button from 'component/Button'
import DateIntervalInput from 'component/DateIntervalInput/NewDateIntevalInput'
import Row from 'component/utils/Row'
import Col from 'component/utils/Col'
import SuccessModal from 'component/Modal/SuccessModal'
import ErrorModal from 'component/Modal/ErrorModal'
import PricebannerWarningModal from 'component/priceBanner/general/PricebannerWarningModal'
import DropdownArrow from 'component/svgComponents/dropdownArrow'
import NewPagination from 'component/pagination/newPagination'
import * as satisfactionSurveyActions from 'action/satisfactionSurvey'
import { colourStyles } from 'utils/selectStyle'
import SatisfactionSurveyTable from './SatisfactionSurveyTable'
import SatisfactionSurveyChart from './SatisfactionSurveyChart'
import 'container/priceBanner/styles.css'
import './styles.css'

const PAGINATION_OPTIONS = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
]

const SatisfactionSurveyContainer = ({
  auth: { user },
  translate,
  list,
  error,
  errorSave,
  fetchAll,
  createEvaluation,
  updateEvaluation,
  deleteEvaluation,
  downloadEvaluationReport,
}) => {
  const { dsToken } = user
  const [openAddModal, setOpenAddModal] = useState(false)
  const [openSuccessModal, setOpenSuccessModal] = useState(false)
  const [errorDetails, setErrorDetails] = useState('')
  const [dateRange, setDateRange] = useState([])
  const [formError, setFormError] = useState(false)
  const [selectedEvaluation, setSelectedEvaluation] = useState(null)
  const [openDetailsModal, setOpenDetailsModal] = useState(false)
  const [pendingCreate, setPendingCreate] = useState(null)
  const [openActiveWarning, setOpenActiveWarning] = useState(false)
  const [conflictingEvaluation, setConflictingEvaluation] = useState(null)
  const [conflictSource, setConflictSource] = useState(null)
  const [editingEvaluation, setEditingEvaluation] = useState(null)
  const [openEditModal, setOpenEditModal] = useState(false)
  const [editDateRange, setEditDateRange] = useState([])
  const [editFormError, setEditFormError] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [successMessageKey, setSuccessMessageKey] = useState('satisfactionSurvey.success.create')
  const [page, setPage] = useState(1)
  const [optionSize, setOptionSize] = useState(10)

  const getErrorMessage = (currentError, fallbackKey) =>
    currentError?.message?.dsMessage ||
    currentError?.message?.message ||
    currentError?.dsMessage ||
    (typeof currentError?.message === 'string' ? currentError.message : null) ||
    translate(fallbackKey)

  useEffect(() => {
    fetchAll(dsToken)
  }, [])

  useEffect(() => {
    if (error) {
      setErrorDetails(getErrorMessage(error, 'satisfactionSurvey.error.load'))
    }
  }, [error])

  useEffect(() => {
    if (errorSave) {
      setErrorDetails(getErrorMessage(errorSave, 'satisfactionSurvey.error.create'))
    }
  }, [errorSave])

  useEffect(() => {
    if (!openAddModal) {
      setDateRange([])
      setFormError(false)
    }
  }, [openAddModal])

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((list ?? []).length / optionSize))
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [list, optionSize, page])

  const handleCreate = async (payload) => {
    try {
      await createEvaluation(dsToken, payload)
      setOpenAddModal(false)
      setSuccessMessageKey('satisfactionSurvey.success.create')
      setOpenSuccessModal(true)
      fetchAll(dsToken, false)
    } catch (createError) {
      setErrorDetails(getErrorMessage(createError, 'satisfactionSurvey.error.create'))
    }
  }

  const handleOpenEdit = (record) => {
    setEditingEvaluation(record)
    setEditDateRange([moment(record.dtBegin), moment(record.dtEnd)])
    setEditFormError(false)
    setOpenEditModal(true)
  }

  const handleConfirmEdit = async () => {
    if (!Array.isArray(editDateRange) || editDateRange.length !== 2 || !editDateRange[1]) {
      setEditFormError(true)
      return
    }

    const isActive = moment().isSameOrAfter(moment(editingEvaluation.dtBegin), 'day')
    const newBegin = isActive
      ? moment(editingEvaluation.dtBegin)
      : editDateRange[0]
    const newEnd = editDateRange[1]

    const conflict = (list ?? []).find((item) => {
      if (!item.dtBegin || !item.dtEnd) return false
      if (item.idEvaluation === editingEvaluation.idEvaluation) return false
      return (
        newBegin.isSameOrBefore(moment(item.dtEnd), 'day') &&
        newEnd.isSameOrAfter(moment(item.dtBegin), 'day')
      )
    })

    if (conflict) {
      setConflictingEvaluation(conflict)
      setConflictSource('edit')
      setOpenEditModal(false)
      setOpenActiveWarning(true)
      return
    }

    const payload = {
      idEvaluation: editingEvaluation.idEvaluation,
      dtBegin: newBegin.format('YYYY-MM-DD'),
      dtEnd: newEnd.format('YYYY-MM-DD'),
    }

    try {
      await updateEvaluation(dsToken, payload)
      setOpenEditModal(false)
      setSuccessMessageKey('satisfactionSurvey.success.update')
      setOpenSuccessModal(true)
      fetchAll(dsToken, false)
    } catch (editError) {
      setErrorDetails(getErrorMessage(editError, 'satisfactionSurvey.error.update'))
    }
  }

  const handleConfirmCreate = () => {
    const hasInvalidRange =
      !Array.isArray(dateRange) ||
      dateRange.length !== 2 ||
      !dateRange[0] ||
      !dateRange[1]

    if (hasInvalidRange) {
      setFormError(true)
      return
    }

    const [newBegin, newEnd] = dateRange
    const conflict = (list ?? []).find((item) => {
      if (!item.dtBegin || !item.dtEnd) return false
      return (
        newBegin.isSameOrBefore(moment(item.dtEnd), 'day') &&
        newEnd.isSameOrAfter(moment(item.dtBegin), 'day')
      )
    })

    if (conflict) {
      setConflictingEvaluation(conflict)
      setConflictSource('create')
      setOpenAddModal(false)
      setOpenActiveWarning(true)
      return
    }

    setOpenAddModal(false)
    setPendingCreate({
      dtBegin: newBegin.format('YYYY-MM-DD'),
      dtEnd: newEnd.format('YYYY-MM-DD'),
    })
  }

  const confirmCreate = () => {
    handleCreate(pendingCreate)
    setPendingCreate(null)
  }

  const handleDelete = async () => {
    try {
      await deleteEvaluation(dsToken, pendingDelete.idEvaluation)
      setPendingDelete(null)
      setSuccessMessageKey('satisfactionSurvey.success.delete')
      setOpenSuccessModal(true)
      fetchAll(dsToken, false)
    } catch (deleteError) {
      setPendingDelete(null)
      setErrorDetails(getErrorMessage(deleteError, 'satisfactionSurvey.error.delete'))
    }
  }

  const handleDownload = async (record) => {
    try {
      await downloadEvaluationReport(
        dsToken,
        record.idEvaluation,
        `avaliacoes_pesquisa_${record.idEvaluation}.csv`
      )
    } catch (downloadError) {
      setErrorDetails(
        getErrorMessage(downloadError, 'satisfactionSurvey.error.download')
      )
    }
  }

  const handleOpenDetails = (record) => {
    setSelectedEvaluation(record)
    setOpenDetailsModal(true)
  }

  const formatDateTime = (value) => {
    if (!value) {
      return '-'
    }

    return moment(value).format('DD/MM/YYYY')
  }

  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === '') {
      return '0,00'
    }

    return Number(value).toFixed(2).replace('.', ',')
  }

  const renderSuccessModal = () => {
    if (!openSuccessModal) {
      return null
    }

    return (
      <SuccessModal
        fillButton
        center
        size='lg'
        buttonText={translate('ok_button')}
        msg={translate(successMessageKey)}
        t={translate}
        handleClose={() => setOpenSuccessModal(false)}
      />
    )
  }

  const renderErrorModal = () => {
    if (!errorDetails) {
      return null
    }

    return (
      <ErrorModal
        center
        size='sm'
        msg={translate('satisfactionSurvey.error.title')}
        details={errorDetails}
        buttonText={translate('close')}
        t={translate}
        handleClose={() => setErrorDetails('')}
      />
    )
  }

  const renderDetailsItem = (label, value) => (
    <div className='satisfaction-survey-details-item'>
      <span className='satisfaction-survey-details-label'>{label}</span>
      <span className='satisfaction-survey-details-value'>{value}</span>
    </div>
  )

  const renderDetailsModal = () => {
    if (!openDetailsModal || !selectedEvaluation) {
      return null
    }

    return (
      <Modal
        title={translate('satisfactionSurvey.details.title')}
        size='lg'
        isOpen={openDetailsModal}
        onCloseModal={() => setOpenDetailsModal(false)}
        useControlledState
        className='pricebanner-modal satisfaction-survey-modal satisfaction-survey-details-modal'
        showFooter={false}
      >
        <div className='satisfaction-survey-details-rows'>
          <div className='satisfaction-survey-details-grid satisfaction-survey-details-grid-compact'>
            {renderDetailsItem(
              translate('satisfactionSurvey.table.description'),
              selectedEvaluation.description || '-'
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.table.begin'),
              formatDateTime(selectedEvaluation.dtBegin)
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.table.end'),
              formatDateTime(selectedEvaluation.dtEnd)
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.table.quantity'),
              selectedEvaluation.quantity ?? 0
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.table.average'),
              formatDecimal(selectedEvaluation.average)
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.table.nps'),
              formatDecimal(selectedEvaluation.nps)
            )}
          </div>

          <div className='satisfaction-survey-details-grid satisfaction-survey-details-grid-compact'>
            {renderDetailsItem(
              translate('satisfactionSurvey.details.promoters'),
              selectedEvaluation.promoters ?? 0
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.details.promotersPercent'),
              `${formatDecimal(selectedEvaluation.promotersPercent)}%`
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.details.detractors'),
              selectedEvaluation.detractors ?? 0
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.details.detractorsPercent'),
              `${formatDecimal(selectedEvaluation.detractorsPercent)}%`
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.details.neutrals'),
              selectedEvaluation.neutrals ?? 0
            )}
            {renderDetailsItem(
              translate('satisfactionSurvey.details.neutralsPercent'),
              `${formatDecimal(selectedEvaluation.neutralsPercent)}%`
            )}
          </div>

          {selectedEvaluation.scoreDistribution?.length > 0 && (
            <div className='satisfaction-survey-chart-section'>
              <p className='satisfaction-survey-chart-title'>
                {translate('satisfactionSurvey.chart.title')}
              </p>
              <SatisfactionSurveyChart data={selectedEvaluation.scoreDistribution} />
            </div>
          )}
        </div>
      </Modal>
    )
  }

  const renderCreateModal = () => {
    if (!openAddModal) {
      return null
    }

    return (
      <Modal
        title={translate('satisfactionSurvey.modal.title')}
        size='md'
        isOpen={openAddModal}
        onCloseModal={() => setOpenAddModal(false)}
        useControlledState
        className='pricebanner-modal satisfaction-survey-modal'
        confirmButtonText={translate('save_button_label')}
        cancelButtonText={translate('cancel_button')}
        onConfirm={handleConfirmCreate}
        noCloseOnConfirm
      >
        <div className='pricebanner-form-container satisfaction-survey-form-container'>
          <DateIntervalInput
            label={translate('satisfactionSurvey.form.period')}
            initialValue={dateRange}
            onChange={(value) => {
              setDateRange(value)
              setFormError(false)
            }}
            required
            error={formError ? 'error' : undefined}
            errorMsg={formError ? translate('mandatory') : undefined}
            noSelect
            disableAutoClose
            minDate={new Date()}
            useMinDate
            user={user}
          />
        </div>
      </Modal>
    )
  }

  const renderEditModal = () => {
    if (!openEditModal || !editingEvaluation) return null

    const isActive = moment().isSameOrAfter(moment(editingEvaluation.dtBegin), 'day')

    return (
      <Modal
        title={translate('satisfactionSurvey.edit.title')}
        size='md'
        isOpen={openEditModal}
        onCloseModal={() => setOpenEditModal(false)}
        useControlledState
        className='pricebanner-modal satisfaction-survey-modal'
        confirmButtonText={translate('save_button_label')}
        cancelButtonText={translate('cancel_button')}
        onConfirm={handleConfirmEdit}
        noCloseOnConfirm
      >
        <div className='pricebanner-form-container satisfaction-survey-form-container'>
          {isActive && (
            <p className='satisfaction-survey-edit-active-info'>
              {translate('satisfactionSurvey.edit.active.info')}
            </p>
          )}
          <DateIntervalInput
            label={translate('satisfactionSurvey.form.period')}
            initialValue={editDateRange}
            onChange={(value) => {
              setEditDateRange(value)
              setEditFormError(false)
            }}
            required
            error={editFormError ? 'error' : undefined}
            errorMsg={editFormError ? translate('mandatory') : undefined}
            noSelect
            disableAutoClose
            minDate={new Date()}
            useMinDate
            user={user}
            lockedStartDate={isActive ? moment(editingEvaluation.dtBegin).toDate() : undefined}
          />
        </div>
      </Modal>
    )
  }

  const renderDeleteConfirmModal = () => {
    if (!pendingDelete) return null

    return (
      <Modal
        isOpen
        size='sm'
        center
        showHeader={false}
        showFooter={false}
        onCloseModal={() => setPendingDelete(null)}
        renderChildrenWithProps={({ onClose }) => (
          <div className='satisfaction-survey-warning-modal satisfaction-survey-warning-modal-danger'>
            <i className='glyph glyph-trash-can satisfaction-survey-warning-modal-icon satisfaction-survey-warning-modal-icon-danger' />
            <p className='satisfaction-survey-warning-modal-title satisfaction-survey-warning-modal-title-danger'>
              {translate('satisfactionSurvey.delete.confirm.title')}
            </p>
            <div className='satisfaction-survey-warning-modal-content'>
              <p className='satisfaction-survey-warning-modal-message'>
                {translate('satisfactionSurvey.delete.confirm.message')}
              </p>
              <p className='satisfaction-survey-warning-modal-detail'>
                <strong>{translate('satisfactionSurvey.form.begin')}:</strong>
                <span>{formatDateTime(pendingDelete.dtBegin)}</span>
              </p>
              <p className='satisfaction-survey-warning-modal-detail'>
                <strong>{translate('satisfactionSurvey.form.end')}:</strong>
                <span>{formatDateTime(pendingDelete.dtEnd)}</span>
              </p>
            </div>
            <div className='button-row'>
              <Button
                className='btn satisfaction-survey-cancel-btn'
                text={translate('cancel_button')}
                onClick={onClose}
              />
              <Button
                className='btn satisfaction-survey-delete-confirm-btn'
                text={translate('satisfactionSurvey.delete.confirm.button')}
                onClick={() => {
                  handleDelete()
                  onClose()
                }}
              />
            </div>
          </div>
        )}
      />
    )
  }

  const renderConfirmModal = () => {
    if (!pendingCreate) {
      return null
    }

    return (
      <Modal
        isOpen
        size='sm'
        center
        showHeader={false}
        showFooter={false}
        onCloseModal={() => setPendingCreate(null)}
        renderChildrenWithProps={({ onClose }) => (
          <div className='satisfaction-survey-warning-modal'>
            <i className='glyph glyph-exclamation-mark satisfaction-survey-warning-modal-icon' />
            <p className='satisfaction-survey-warning-modal-title'>
              {translate('satisfactionSurvey.confirm.title')}
            </p>
            <div className='satisfaction-survey-warning-modal-content'>
              <p className='satisfaction-survey-warning-modal-message'>
                {translate('satisfactionSurvey.confirm.message')}
              </p>
              <p className='satisfaction-survey-warning-modal-detail'>
                <strong>{translate('satisfactionSurvey.form.begin')}:</strong>
                <span>{moment(pendingCreate.dtBegin).format('DD/MM/YYYY')}</span>
              </p>
              <p className='satisfaction-survey-warning-modal-detail'>
                <strong>{translate('satisfactionSurvey.form.end')}:</strong>
                <span>{moment(pendingCreate.dtEnd).format('DD/MM/YYYY')}</span>
              </p>
            </div>
            <div className='button-row'>
              <Button
                text={translate('cancel_button')}
                onClick={onClose}
              />
              <Button
                className='btn satisfaction-survey-warning-modal-confirm-btn'
                text={translate('confirm_button_label')}
                onClick={() => {
                  confirmCreate()
                  onClose()
                }}
              />
            </div>
          </div>
        )}
      />
    )
  }

  const renderActiveWarningModal = () => {
    if (!openActiveWarning) {
      return null
    }

    const description = conflictingEvaluation?.description || '-'
    const detailsAsHtml = translate('satisfactionSurvey.activeWarning.message')
      .replace('{description}', description)
      .replace('{startDate}', formatDateTime(conflictingEvaluation?.dtBegin))
      .replace('{endDate}', formatDateTime(conflictingEvaluation?.dtEnd))

    const handleCloseWarning = () => {
      setOpenActiveWarning(false)
      setConflictingEvaluation(null)
      setConflictSource(null)
    }

    const handleNewRegister = () => {
      setOpenActiveWarning(false)
      setConflictingEvaluation(null)
      if (conflictSource === 'edit') {
        setOpenEditModal(true)
      } else {
        setOpenAddModal(true)
      }
      setConflictSource(null)
    }

    return (
      <PricebannerWarningModal
        isOpen
        onClose={handleCloseWarning}
        onNewRegister={handleNewRegister}
        onSeeExisting={handleCloseWarning}
        details={detailsAsHtml}
        detailsAsHtml={detailsAsHtml}
        translate={translate}
        customTitle={translate('satisfactionSurvey.activeWarning.title')}
        customQuestion={translate('satisfactionSurvey.activeWarning.question')}
      />
    )
  }

  const renderPagination = () => {
    if (!hasData) {
      return null
    }

    const totalCount = (list ?? []).length
    const firstItem = totalCount === 0 ? 0 : (currentPage - 1) * optionSize + 1
    const lastItem = Math.min(currentPage * optionSize, totalCount)
    const DropdownIndicator = (props) => (
      <components.DropdownIndicator {...props}>
        <DropdownArrow />
      </components.DropdownIndicator>
    )

    return (
      <Row className='new-relatory-row stock-transfer-pagination'>
        <Col className='align-center margin-bottom'>
          <NewPagination
            navClass='pagination new-relatory'
            count={totalCount}
            current={currentPage}
            getPage={setPage}
            pages={pageCount}
          />

          <div className='datasheet-manager-pagination-size-selector show-pages-new-relatory'>
            <Col className='show-per-page-text' xs={7}>
              {translate('pagination.size.selector')}
            </Col>
            <Col xs={4}>
              <SizeSelect
                components={{ DropdownIndicator }}
                isSearchable={false}
                styles={colourStyles}
                classNamePrefix='react-select'
                onChange={(option) => {
                  setOptionSize(option.value)
                  setPage(1)
                }}
                options={PAGINATION_OPTIONS}
                value={PAGINATION_OPTIONS.find((item) => item.value === optionSize)}
                menuPlacement='top'
              />
            </Col>
          </div>

          <div className='datasheet-manager-pagination-size-selector show-items-new-relatory satisfaction-survey-pagination-info'>
            <Col>{`${translate('pagination.size.show')} ${firstItem} - ${lastItem} ${translate(
              'pagination.size.of'
            )} ${totalCount} ${translate('pagination.size.items')}`}</Col>
          </div>
        </Col>
      </Row>
    )
  }

  const hasData = (list ?? []).length > 0
  const pageCount = Math.max(1, Math.ceil((list ?? []).length / optionSize))
  const currentPage = Math.min(page, pageCount)
  const displayedList = (list ?? []).slice(
    (currentPage - 1) * optionSize,
    currentPage * optionSize
  )

  return (
    <div className='stock-adjustment satisfaction-survey'>
      <div className='stock-adjustment-header'>
        <ContentHeader>{translate('satisfactionSurvey.title')}</ContentHeader>
      </div>

      <div className='spacing-end satisfaction-survey-actions'>
        <div>
          <button
            className='btn filter-mission-block satisfaction-survey-primary-button'
            onClick={() => setOpenAddModal(true)}
          >
            {translate('satisfactionSurvey.new')}
          </button>
        </div>
      </div>

      <div className={`satisfaction-survey-table-section ${!hasData ? 'satisfaction-survey-empty' : ''}`}>
        {hasData ? (
          <SatisfactionSurveyTable
            items={displayedList}
            translate={translate}
            onViewDetails={handleOpenDetails}
            onEdit={handleOpenEdit}
            onDelete={(record) => setPendingDelete(record)}
            onDownload={handleDownload}
          >
            {renderPagination()}
          </SatisfactionSurveyTable>
        ) : (
          <div className='relatory-no-data pictogram-empty no-register satisfaction-survey-empty-state'>
            <p className='no-register-title'>
              {translate('satisfactionSurvey.empty.title')}
            </p>
            <p className='no-register-subtitle'>
              {translate('satisfactionSurvey.empty.subtitle')}
            </p>
          </div>
        )}
      </div>

      {renderCreateModal()}
      {renderEditModal()}
      {renderDeleteConfirmModal()}
      {renderConfirmModal()}
      {renderActiveWarningModal()}
      {renderDetailsModal()}
      {renderSuccessModal()}
      {renderErrorModal()}
    </div>
  )
}

SatisfactionSurveyContainer.propTypes = {
  auth: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  fetchAll: PropTypes.func.isRequired,
  createEvaluation: PropTypes.func.isRequired,
  updateEvaluation: PropTypes.func.isRequired,
  deleteEvaluation: PropTypes.func.isRequired,
  downloadEvaluationReport: PropTypes.func.isRequired,
  list: PropTypes.array,
  error: PropTypes.object,
  errorSave: PropTypes.object,
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  list: state.satisfactionSurveyReducer?.list ?? [],
  error: state.satisfactionSurveyReducer?.error ?? null,
  errorSave: state.satisfactionSurveyReducer?.errorSave ?? null,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchAll: satisfactionSurveyActions.fetchAll,
      createEvaluation: satisfactionSurveyActions.createEvaluation,
      updateEvaluation: satisfactionSurveyActions.updateEvaluation,
      deleteEvaluation: satisfactionSurveyActions.deleteEvaluation,
      downloadEvaluationReport: satisfactionSurveyActions.downloadEvaluationReport,
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    SatisfactionSurveyContainer
  )
)

export { SatisfactionSurveyContainer }
